import React from "react";
import { useState, useMemo, useRef } from "react";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { Question } from "../Components/Question";
import { MainTitle } from "../Components/MainTitle";
import { QuestionImage } from "../Components/QuestionImage";
import { Utilities } from "@learningblocks/exp-constants";
import { Footer } from "../Components/Footer";
import { SubmitButton } from "../Components/SubmitButton";
import { TotalAttempts } from "../Components/TotalAttempts";
import { useIntl } from "react-intl";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import "./style.scss";

const { QUESTION_CONTENTS } = Utilities;

interface Selection {
  index: number;
  value: string;
}

interface DropDown {
  answer: string;
  top: string;
  left: string;
  ariaLabel?: string;
}

interface DiagramContent {
  image: {
    altText: string;
    [key: string]: any;
  };
  dropDowns: DropDown[];
}

interface DiagramQuestionProps {
  id: string;
  selections?: Selection[];
  content?: string[];
  diagram?: DiagramContent;
  className?: string;
  preview?: boolean;
  complete?: boolean;
  feedback?: string | object;
  showAttempts?: boolean;
  totalAttempts?: number;
  submitSelections: (payload: {
    componentID: string;
    selections: Selection[];
  }) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export function DiagramQuestion(props: DiagramQuestionProps) {
  const intl = useIntl();
  const [selections, setSelections] = useState<Selection[]>(
    props.selections || []
  );
  const [announcements, setAnnouncements] = useState<string>("");
  const liveRegionRef = useRef<HTMLDivElement>(null);

  const shuffledOptions = useMemo(() => {
    if (!props || !props.content) return [];

    const diagram = props.content.includes("diagram") ? props.diagram : null;
    if (!diagram || !diagram.dropDowns) return [];

    const uniqueAnswers = diagram.dropDowns.map((d) => d.answer);

    // Shuffle using Fisher-Yates
    for (let i = uniqueAnswers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [uniqueAnswers[i], uniqueAnswers[j]] = [
        uniqueAnswers[j],
        uniqueAnswers[i],
      ];
    }

    return uniqueAnswers;
  }, [props.content, props.diagram]);

  const onSubmit = () => {
    props.submitSelections({
      componentID: props.id,
      selections: selections,
    });

    // Focus management: Return focus to first incorrect dropdown after submission
    setTimeout(() => {
      const container = document.getElementById(`diagram-${props.id}`);
      if (container) {
        // Find first dropdown with incorrect or no selection
        const incorrectDropdown = container.querySelector(
          'select[aria-invalid="true"], select[value=""]'
        ) as HTMLSelectElement;

        if (incorrectDropdown) {
          incorrectDropdown.focus();
        }
      }
    }, 100);
  };

  const renderQuestion = (key: string | number, question: string) => (
    <Question key={key} text={question} className="diagram__question" />
  );

  const renderText = (key: string | number, text: string) => (
    <Question key={key} text={text} className="diagram__content" />
  );

  const renderDiagram = (key: string | number, content: DiagramContent) => (
    <div key={key} className="diagram__image-wrapper">
      <QuestionImage
        image={content.image}
        className="diagram__image"
        preview={props.preview}
      />
      <fieldset className="diagram__dropdowns-fieldset">
        <legend className="diagram__dropdowns-legend">
          {intl.formatMessage({
            id: "diagram.dropdowns.legend",
            defaultMessage:
              "Identify anatomical structures in the diagram by selecting the correct name for each labeled structure",
            description: "Legend for diagram dropdowns fieldset",
          })}
        </legend>
        {content.dropDowns &&
          content.dropDowns.map((dropdown, index) => {
            const matched = selections.find((s) => s.index === index);

            const isSubmitted = props.selections && props.selections.length > 0;
            const isCorrectSelection =
              isSubmitted &&
              matched?.value === dropdown.answer &&
              props.selections?.some(
                (item) => item.index === index && item.value === matched?.value
              );

            const isIncorrectSelection =
              isSubmitted &&
              matched?.value &&
              matched?.value !== dropdown.answer &&
              props.selections?.some(
                (item) => item.index === index && item.value === matched?.value
              );

            const getAriaLabel = () => {
              const baseLabel =
                dropdown.ariaLabel ||
                intl.formatMessage({
                  id: "diagram.dropdown.generic.label",
                  defaultMessage:
                    "Select the name of this anatomical structure",
                  description:
                    "Generic label for diagram dropdown when no specific description is available",
                });

              if (isCorrectSelection) {
                return `${baseLabel} ${intl.formatMessage({
                  id: "diagram.dropdown.correct",
                  defaultMessage: "Correct answer selected.",
                  description: "Correct selection announcement",
                })}`;
              }

              if (isIncorrectSelection) {
                return `${baseLabel} ${intl.formatMessage({
                  id: "diagram.dropdown.incorrect",
                  defaultMessage: "Incorrect answer selected.",
                  description: "Incorrect selection announcement",
                })}`;
              }

              return baseLabel;
            };

            const getAriaDescribedBy = () => {
              if (isCorrectSelection) return `correct-feedback-${index}`;
              if (isIncorrectSelection) return `incorrect-feedback-${index}`;
              return undefined;
            };

            return (
              <div
                className="diagram__input-overlay"
                style={{ top: dropdown.top, left: dropdown.left }}
                key={index}
              >
                <select
                  id={`diagram-select-${props.id}-${index}`}
                  aria-label={getAriaLabel()}
                  aria-describedby={getAriaDescribedBy()}
                  aria-invalid={isIncorrectSelection ? "true" : "false"}
                  value={selections.find((s) => s.index === index)?.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelections((prev) => {
                      const filtered = prev.filter(
                        (item) => item.index !== index
                      );
                      if (value === "") return filtered;
                      return [...filtered, { index, value }];
                    });

                    // Announce selection for screen readers
                    if (value) {
                      const structureDescription =
                        dropdown.ariaLabel ||
                        intl.formatMessage({
                          id: "diagram.dropdown.generic.structure",
                          defaultMessage: "anatomical structure",
                          description:
                            "Generic description for anatomical structure",
                        });
                      setAnnouncements(
                        intl.formatMessage(
                          {
                            id: "diagram.selection.announced",
                            defaultMessage: "Selected {value} for {structure}",
                            description: "Selection announcement",
                          },
                          { value, structure: structureDescription }
                        )
                      );
                    }
                  }}
                  className={`diagram__dropdown ${
                    isCorrectSelection ? "diagram__dropdown--correct" : ""
                  } ${
                    isIncorrectSelection ? "diagram__dropdown--incorrect" : ""
                  }`}
                  disabled={isCorrectSelection}
                  tabIndex={isCorrectSelection ? -1 : 0}
                >
                  <option value="" disabled>
                    {intl.formatMessage({
                      id: "diagram.dropdown.placeholder",
                      defaultMessage: "- Select -",
                      description: "Dropdown placeholder text",
                    })}
                  </option>
                  {shuffledOptions.map((option, optionIndex) => (
                    <option key={optionIndex} value={option}>
                      {option}
                    </option>
                  ))}
                </select>

                {/* Hidden feedback elements for screen readers */}
                {isCorrectSelection && (
                  <div
                    id={`correct-feedback-${index}`}
                    className="diagram__sr-only"
                    aria-live="polite"
                  >
                    {intl.formatMessage({
                      id: "diagram.feedback.correct",
                      defaultMessage: "Correct answer",
                      description: "Correct answer feedback",
                    })}
                  </div>
                )}

                {isIncorrectSelection && (
                  <div
                    id={`incorrect-feedback-${index}`}
                    className="diagram__sr-only"
                    aria-live="polite"
                  >
                    {intl.formatMessage({
                      id: "diagram.feedback.incorrect",
                      defaultMessage: "Incorrect answer, please try again",
                      description: "Incorrect answer feedback",
                    })}
                  </div>
                )}
              </div>
            );
          })}
      </fieldset>
    </div>
  );

  const renderFeedback = () => {
    return (
      props.complete && (
        <div
          className="diagram__feedback"
          role="status"
          aria-live="polite"
          aria-label={intl.formatMessage({
            id: "diagram.feedback.label",
            defaultMessage: "Question feedback",
            description: "Label for feedback section",
          })}
        >
          {typeof props.feedback === "string" && props.feedback}
          {typeof props.feedback === "object" && (
            <RichText value={props.feedback as unknown as string} />
          )}
        </div>
      )
    );
  };

  const renderByType = (type: string, key: string | number, content: any) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key, content);
      case QUESTION_CONTENTS.TEXT:
        return renderText(key, content);
      case QUESTION_CONTENTS.DIAGRAM:
        return renderDiagram(key, content);
      case QUESTION_CONTENTS.FEEDBACK:
        return renderFeedback();
      case QUESTION_CONTENTS.CONTENT_LIST:
        return content.map((listItem: any, subKey: number) =>
          renderByType(listItem.type, `${key}_${subKey}`, listItem.content)
        );
      default:
        return null;
    }
  };

  const wrapperClass = `diagram ${props.className || ""}`;

  return (
    <div id={`diagram-${props.id}`}>
      <QuestionWrapper className={wrapperClass}>
        {/* Live region for announcements */}
        <div
          ref={liveRegionRef}
          className="diagram__sr-only"
          aria-live="polite"
          aria-atomic="true"
        >
          {announcements}
        </div>

        <MainTitle
          title={intl.formatMessage({
            id: "DIAGRAM QUESTION",
            defaultMessage: "DIAGRAM QUESTION",
            description: "",
          })}
          className="diagram__mainTitle"
        />
        {props.content?.map((type, key) =>
          renderByType(type, key, props[type])
        )}
        <Footer className="diagram__footer">
          {props.showAttempts && (
            <TotalAttempts
              totalAttempts={props.totalAttempts}
              className="diagram__footer__totalAttempts"
            />
          )}
          {props.totalAttempts === 0 ? (
            <SubmitButton
              label={intl.formatMessage({
                id: "Submit answer",
                defaultMessage: "Submit answer",
                description: "",
              })}
              disabled={!!props.complete}
              onSubmit={onSubmit}
            />
          ) : (
            <SubmitButton
              label={intl.formatMessage({
                id: "Submit answer",
                defaultMessage: "Submit answer",
                description: "",
              })}
              disabled={!!props.complete}
              onSubmit={onSubmit}
            />
          )}
        </Footer>
      </QuestionWrapper>
    </div>
  );
}
